import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
    PowerUserIcon,
    FirstSteps,
    AIExplorer,
    AIThoughtLeader,
    AutomationArchitect,
    BetaTesterBadge,
} from '../assets/AchievementIcons';

const getIconComponent = (name) => {
    switch (name) {
        case 'AI Explorer':
            return <AIExplorer />;
        case 'First Steps':
            return <FirstSteps />;
        case 'Power User':
            return <PowerUserIcon />;
        case 'AI Thought Leader':
            return <AIThoughtLeader />;
        case 'Automation Architect':
            return <AutomationArchitect />;
        case 'Beta Tester':
            return <BetaTesterBadge />;
        default:
            return (
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="40" r="35" stroke="#000" strokeWidth="4" fill="#ddd" />
                    <text
                        x="50%"
                        y="50%"
                        dominantBaseline="middle"
                        textAnchor="middle"
                        fontSize="14"
                        fill="#000"
                    >
                        {name[0]}
                    </text>
                </svg>
            );
    }
};

const ProfileAchievements = ({ achievements }) => {
    const [open, setOpen] = useState(false);
    const [selectedAchievement, setSelectedAchievement] = useState(null);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const handleOpen = (achievement) => {
        setSelectedAchievement(achievement);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedAchievement(null);
    };

    return (
        <Box
            sx={{
                margin: 1,
                paddingTop: 3,
                border: '1px solid',
                borderColor: theme.palette.divider,
                width: '100%',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Typography variant="h6" align="center" gutterBottom>
                Achievements
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    maxWidth: 680,
                    transform: 'scale(0.8)',
                    marginTop: -1,
                }}
            >
                {Array.isArray(achievements) &&
                    achievements.map((achievement, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: 120,
                                padding: 1,
                                borderRadius: 1,
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: 2,
                                },
                            }}
                        >
                            <Button
                                sx={{
                                    textTransform: 'none',
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onClick={() => handleOpen(achievement)}
                            >
                                <Box sx={{ mb: 1 }}>{getIconComponent(achievement.name)}</Box>
                                <Typography variant="body1" fontWeight="bold">
                                    {achievement.name}
                                </Typography>
                            </Button>
                        </Box>
                    ))}
            </Box>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedAchievement?.name}
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Box mb={2}>{selectedAchievement && getIconComponent(selectedAchievement.name)}</Box>
                        <Typography variant="h6" gutterBottom>
                            {selectedAchievement?.name}
                        </Typography>
                        <Typography variant="body1">{selectedAchievement?.description}</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProfileAchievements;
